/**
 * Zahnheld discount rejection reason.
 *
 * /cart/update.js answers every rejected code the same way: `applicable: false`.
 * That covers a typo, an expired code and a valid code that simply lost against
 * a better discount (e.g. the Konfigurator Zweitgerät-Rabatt, an app discount).
 * The last case should read "Du erhältst bereits den maximalen Rabatt", not
 * "ungültig".
 *
 * The Storefront API can tell them apart. We rebuild the customer's cart in an
 * ephemeral cartCreate with only the new code and read Shopify's verdict:
 * - the code applies on its own      -> it lost against another active code
 * - warning DISCOUNT_CODE_NOT_HONOURED -> it lost against an automatic discount
 * - anything else (DISCOUNT_NOT_FOUND, inactive, limits, ...) -> invalid
 *
 * Used by Impact's native cart discount field (assets/theme.js) and by the
 * discount preview (assets/discount-preview.js). Every failure resolves to
 * 'invalid', so the caller always falls back to the generic message.
 *
 * Known blind spot: the ephemeral cart is anonymous. A code that is rejected
 * only for the logged-in customer (e.g. once per customer) reads as
 * 'not_combinable' when another discount is active at the same time.
 */
(function () {
  'use strict';

  if (window.zhDiscountRejection) return;

  const STOREFRONT_PATH = '/api/{version}/graphql.json';

  // The cart field stays aria-busy until this answer is in, so a hung request
  // must not hold it forever. On timeout the fetch rejects and we read 'invalid'.
  const TIMEOUT_MS = 4000;

  const CART_CREATE = `
    mutation zhDiscountRejection($input: CartInput!) {
      cartCreate(input: $input) {
        cart { discountCodes { code applicable } }
        warnings { code }
        userErrors { code }
      }
    }
  `;

  function sameCode(a, b) {
    return String(a || '').toLowerCase() === String(b || '').toLowerCase();
  }

  // Code discounts report type 'discount_code'. Automatic discounts report
  // 'automatic', and app discounts (DiscountOS) report an empty string, so
  // everything that is not a code counts.
  function isAutomatic(application) {
    return !!application && application.type !== 'discount_code';
  }

  /** True when another code or an automatic discount is active in the cart. */
  function hasCompetingDiscount(cart, code) {
    if (!cart) return false;
    const otherCode = (cart.discount_codes || []).some((c) => c.applicable && !sameCode(c.code, code));
    if (otherCode) return true;
    if ((cart.cart_level_discount_applications || []).some(isAutomatic)) return true;
    return (cart.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some((a) => isAutomatic(a.discount_application))
    );
  }

  function toCartLine(item) {
    const line = {
      merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
      quantity: item.quantity
    };
    const attributes = Object.entries(item.properties || {})
      .filter((entry) => entry[1] !== null && entry[1] !== undefined && entry[1] !== '')
      .map((entry) => ({ key: entry[0], value: String(entry[1]) }));
    if (attributes.length) line.attributes = attributes;
    const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
    if (plan && plan.id) line.sellingPlanId = 'gid://shopify/SellingPlan/' + plan.id;
    return line;
  }

  function buildInput(code, cart) {
    const input = {
      lines: (cart.items || []).map(toCartLine),
      discountCodes: [code]
    };
    const country = window.Shopify && window.Shopify.country;
    if (country) input.buyerIdentity = { countryCode: country };
    return input;
  }

  async function askStorefront(code, cart) {
    const vars = window.themeVariables || {};
    const api = vars.storefrontApi || {};
    const domain = vars.shop && vars.shop.domain;
    if (!api.token || !domain) return null;

    const endpoint = 'https://' + domain + STOREFRONT_PATH.replace('{version}', api.version || '2026-04');
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': api.token
      },
      body: JSON.stringify({ query: CART_CREATE, variables: { input: buildInput(code, cart) } }),
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(TIMEOUT_MS) : undefined
    });
    if (!res.ok) return null;
    const json = await res.json();
    return (json && json.data && json.data.cartCreate) || null;
  }

  /**
   * Resolves 'not_combinable' when the rejected code exists and would apply,
   * but another active discount beats it, else 'invalid'.
   */
  async function reasonFor(code, cart) {
    const value = String(code || '').trim();
    if (!value || !hasCompetingDiscount(cart, value)) return 'invalid';
    try {
      const result = await askStorefront(value, cart);
      if (!result || !result.cart) return 'invalid';
      const appliesAlone = (result.cart.discountCodes || []).some((c) => c.applicable && sameCode(c.code, value));
      const notHonoured = (result.warnings || []).some((w) => w.code === 'DISCOUNT_CODE_NOT_HONOURED');
      return appliesAlone || notHonoured ? 'not_combinable' : 'invalid';
    } catch (e) {
      console.warn('[zh-discount-rejection] Storefront check failed', e);
      return 'invalid';
    }
  }

  window.zhDiscountRejection = {
    hasCompetingDiscount: hasCompetingDiscount,
    reasonFor: reasonFor
  };
})();
